import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import logo from "../../assets/logo.png";
import styles from "./Header.module.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_NAV = [
  { to: "/inicial",          label: "Inicial",    roles: ["admin", "superadmin"] },
  { to: "/produtos",         label: "Produtos",   roles: ["admin", "superadmin", "estoque"] },
  { to: "/entrada",          label: "Entrada",    roles: ["admin", "superadmin", "estoque"] },
  { to: "/estoque",          label: "Estoque",    roles: ["admin", "superadmin", "estoque"] },
  { to: "/venda",            label: "Venda",      roles: ["admin", "superadmin", "vendedor"] },
  { to: "/historico-vendas", label: "Histórico de Venda",  roles: ["admin", "superadmin", "vendedor"] },
  { to: "/Aluguel",          label: "Aluguel",    roles: ["admin", "superadmin"] },
];

const ADMIN_NAV = [
  { to: "/calendario",      label: "Calendário" },
  { to: "/cliente",         label: "Área do Cliente" },
  { to: "/etiqueta",        label: "Etiqueta" },
  { to: "/faturamento",     label: "Faturamento" },
  { to: "/meus-clientes",   label: "Meus Clientes" },
  { to: "/orcamento",       label: "Orçamentos" },
  { to: "/precificacao",    label: "Precificação" },
  { to: "/usuarios",        label: "Usuários" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hasRole = (user, roles) =>
  Boolean(user && roles.includes(user.nivel_acesso));

// ─── Sub-components ───────────────────────────────────────────────────────────

const ThemeToggle = ({ theme, onToggle }) => {
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      className={`${styles.themeToggle} ${isDark ? styles.themeToggleDark : ""}`}
      onClick={onToggle}
      aria-label={isDark ? "Ativar modo claro" : "Ativar modo escuro"}
      title={isDark ? "Modo claro" : "Modo escuro"}
    >
      <span className={styles.themeTrack} aria-hidden="true">
        <span className={styles.themeThumb} />
      </span>
      {isDark ? <MoonIcon /> : <SunIcon />}
    </button>
  );
};

const AdminDropdown = ({ links, isOpen, onToggle }) => (
  <div className={`${styles.dropdown} ${isOpen ? styles.dropdownOpen : ""}`}>
    <button
      type="button"
      className={styles.dropdownBtn}
      onClick={onToggle}
      aria-haspopup="menu"
      aria-expanded={isOpen}
    >
      Administração
      <ChevronIcon className={`${styles.caret} ${isOpen ? styles.caretOpen : ""}`} />
    </button>

    <div className={styles.dropdownMenu} role="menu" aria-label="Administração">
      {links.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          role="menuitem"
          className={({ isActive }) =>
            `${styles.dropdownItem} ${isActive ? styles.dropdownItemActive : ""}`
          }
        >
          {label}
        </NavLink>
      ))}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Header = () => {
  const { user, logout }       = useContext(AuthContext);
  const { theme, toggleTheme } = useTheme();
  const navigate               = useNavigate();
  const location               = useLocation();
  const rootRef                = useRef(null);

  const [menuOpen,  setMenuOpen]  = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const navLinks = useMemo(
    () => ROLE_NAV.filter(({ roles }) => hasRole(user, roles)),
    [user]
  );

  const adminLinks = useMemo(
    () => (hasRole(user, ["admin", "superadmin"]) ? ADMIN_NAV : []),
    [user]
  );

  useEffect(() => {
    setMenuOpen(false);
    setAdminOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
        setAdminOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/", { replace: true });
  }, [logout, navigate]);

  const toggleMenu  = useCallback(() => setMenuOpen((v) => !v),  []);
  const toggleAdmin = useCallback(() => setAdminOpen((v) => !v), []);

  const brandTitle = user?.cliente_nome ?? "FluxoEasy";

  return (
    <header ref={rootRef} className={styles.header} role="banner">
      <div className={styles.inner}>

        <Link to="/inicial" className={styles.brand} aria-label="Ir para a página inicial">
          <img src={logo} alt="FluxoEasy" className={styles.logo} />
          <span className={styles.brandTitle}>{brandTitle}</span>
        </Link>

        <button
          type="button"
          className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ""}`}
          onClick={toggleMenu}
          aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={menuOpen}
          aria-controls="header-nav"
        >
          <span className={styles.bar} />
          <span className={styles.bar} />
          <span className={styles.bar} />
        </button>

        <nav
          id="header-nav"
          className={`${styles.nav} ${menuOpen ? styles.navOpen : ""}`}
          aria-label="Navegação principal"
        >
          <div className={styles.navLinks}>
            {navLinks.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
                }
              >
                {label}
              </NavLink>
            ))}

            {adminLinks.length > 0 && (
              <AdminDropdown
                links={adminLinks}
                isOpen={adminOpen}
                onToggle={toggleAdmin}
              />
            )}
          </div>

          <div className={styles.userBox}>
            <ThemeToggle theme={theme} onToggle={toggleTheme} />

            {user && (
              <>
                <span
                  className={styles.username}
                  title={user.nome}
                  aria-label={`Usuário: ${user.nome}`}
                >
                  {user.nome.split(" ")[0].slice(0, 10)}
                </span>
                <button
                  type="button"
                  className={styles.logoutBtn}
                  onClick={handleLogout}
                  aria-label="Sair da conta"
                >
                  <LogoutIcon />
                  Sair
                </button>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;

// ─── Icons ────────────────────────────────────────────────────────────────────

const SunIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
  </svg>
);

const MoonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const ChevronIcon = ({ className }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    className={className} aria-hidden="true" focusable="false">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" focusable="false">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);